import React, { useEffect } from "react";
import { CameraOff, CircleAlert } from "lucide-react";

interface CameraDisconnectedWarningProps {
  show: boolean;
  reason: "user_stopped" | "track_ended" | "permission_denied" | "device_disconnected";
  onClose?: () => void;
}

export const CameraDisconnectedWarning: React.FC<CameraDisconnectedWarningProps> = ({
  show,
  reason,
  onClose,
}) => {
  // Debug logging
  useEffect(() => {
    console.log("CameraDisconnectedWarning props:", { show, reason });
  }, [show, reason]);

  if (!show) return null;

  const getContent = () => {
    switch (reason) {
      case "user_stopped":
        return {
          description: "You have manually turned off your camera. This video call session requires an active camera feed.",
        };
      case "permission_denied":
        return {
          description: "Camera permission was denied or revoked. This video call session requires camera permissions.",
        };
      case "device_disconnected":
        return {
          description: "Your camera device has been disconnected from the system.",
        };
      case "track_ended":
      default:
        return {
          description: "Camera feed is required for this interview. Please contact the recruiter if you believe this was in error.",
        };
    }
  };

  const content = getContent();

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="w-[480px] h-[338px] p-10 bg-[#131314] rounded-3xl border border-[#ec3f3f] flex flex-col justify-start items-start gap-2.5 overflow-hidden">
        <div className="self-stretch h-[258px] flex flex-col justify-center items-center gap-10">
          {/* Main content section */}
          <div className="self-stretch h-[180px] flex flex-col justify-start items-center gap-3">
            {/* Icon container with background circle */}
            <div className="w-[60px] h-[60px] relative flex justify-center items-center">
              <div className="w-[60px] h-[60px] absolute bg-[#ec4040]/20 rounded-full" />
              <CameraOff
                className="w-7 h-7 text-[#ec4040] z-10"
                strokeWidth={2}
                aria-label="Camera disconnected icon"
              />
            </div>

            {/* Title and subtitle section */}
            <div className="self-stretch h-[52px] flex flex-col justify-start items-center gap-1">
              <h1 className="text-center text-white text-2xl font-bold font-satoshi leading-7">
                Camera Disconnected
              </h1>
              <h2 className="self-stretch text-center text-white text-base font-normal font-satoshi leading-tight">
                Video Monitoring Violation
              </h2>
            </div>

            {/* Description text */}
            <p className="self-stretch text-center text-white/70 text-base font-normal font-satoshi leading-normal">
              {content.description}
            </p>
          </div>

          {/* Alert */}
          <div className="px-4 py-2 bg-[#ec4040]/20 rounded flex justify-start items-center gap-2">
            <CircleAlert
              className="w-5 h-5 text-[#ec4040]"
              strokeWidth={2}
              aria-hidden="true"
            />
            <span className="text-center text-[#ec4040] text-base font-normal font-satoshi">
              Interview Ended
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};